<template>
  <div class="template-component">
    <!-- Add your template content here -->

    <div v-if="templatesLoading"
      style="position: relative; top: 100px;">
      <spinner />
    </div>

    <div v-if="templates.length === 0 && !templatesLoading">
      <div class="is-flex py-4 m-1 is-justify-content-center is-align-items-center has-text-grey">
        <div>
          <div class="is-flex is-justify-content-center my-2 has-text-grey-lighter">
            <span class="material-symbols-rounded is-size-1">
              note_add
            </span>
          </div>

          <!-- show a different message depending on if user has permissions to add templates -->
          <span v-if="!!permussions?.hasAdd"
            class="is-flex is-align-items-center is-justify-content-center is-flex-direction-column has-text-centered">
            <span>No templates yet for this vehicle type, but you can create one!</span>
            <span>Just add items to your quote, click More options (top right),</span>
            <span>and select Save Template for future use.</span>
          </span>
          <span v-else
            class="is-flex is-align-items-center is-justify-content-center is-flex-direction-column has-text-centered">
            <span>No templates yet!</span>
            <span>Please contact an admin or support staff to create one.</span>
          </span>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="is-flex p-1 m-1 is-justify-content-space-between is-align-items-center template-list-item"
        v-for="i in templates"
        :key="i.templateId">

        <div class="is-flex">
          <div class="ml-2">{{ i.name }}</div>
        </div>

        <div class="is-flex is-align-items-center py-1">
          <!-- <button class="button is-ghost is-small p-0 has-text-grey is-clickable mr-1"
            @click="handleTemplateClick(i)">
            <span class="material-symbols-outlined">visibility</span>
          </button> -->

          <button class="button is-ghost is-small tooltip p-0 has-text-primary is-clickable mr-2"
            @click="handleTemplateClick(i)"
            data-tooltip="View and Add Items">

            <span class="material-symbols-outlined">add</span>
          </button>

          <button class="button is-ghost is-small tooltip px-0 mr-1 has-text-grey-lighter"
            @click="handleDeleteTemplateClick(i)"
            :disabled="!permussions?.hasDelete"
            data-tooltip="Delete"
            style="border: none; height: auto; background: transparent;">
            <span class="material-symbols-rounded "
              @mouseenter="$event.target.classList.add('has-text-danger')"
              @mouseleave="$event.target.classList.remove('has-text-danger')">
              delete
            </span>
          </button>
        </div>
      </div>
    </div>

    <confirm-modal v-if="templateDeleted"
      :active.sync="isConfirmModalActive"
      @ok="deleteTemplate(true)"
      @cancel="deleteTemplate(false)"
      :ok-text="'Yes'"
      :cancel-text="'No'">
      <p slot="text-title">Delete Quote Template</p>
      <p slot="text-content"
        class="has-text-danger">
        Quote Template <span class="has-text-weight-bold">{{ templateDeleted.name }}</span>
        will be deleted. Continue?
      </p>
    </confirm-modal>

    <confirm-modal v-if="templateToAdd"
      :active.sync="addTemplateConfirmModalActive"
      @ok="addItemsFromTemplate"
      @cancel="cancelAddItems"
      :ok-text="'Ok'"
      :cancel-text="'Cancel'">
      <p slot="text-title"
        class="has-text-weight-semibold">
        Add Items from: {{ templateToAdd.name }}
      </p>
      <div slot="text-content">
        <p>
          You are about to import {{ templateItems?.length }} items into Quote {{ quoteNo }}.
        </p>
        <p class="mt-2">
          Press OK to confirm or Cancel to go back.
        </p>

        <p class="mt-3 is-size-6 has-text-grey">
          <span class="has-text-weight-semibold">Note:</span>
          This action cannot be undone.
        </p>

      </div>
    </confirm-modal>

    <template-items-modal v-if="isTemplateItemsModalActive"
      :active.sync="isTemplateItemsModalActive"
      :selected-template="selectedTemplate"
      :rates="rates"
      @addTemplateItemsCustom="obj => $emit('addTemplateItemsCustom', obj)" />

  </div>
</template>

<script>
import QuoteTemplateService from '@/services/QuoteTemplateService'
import { QuoteTemplateListColumns } from '@/views/quotetemplate/columns.js'
import { QuoteTemplateTypeKeys } from '@/enums/QuoteTemplateTypes'
import TemplateItemsModal from './TemplateItemsModal.vue'
import Spinner from '../../../../components/Spinners/Spinner.vue'
import ConfirmModal from '../../../../components/BulmaModal/ConfirmModal.vue'
import { EventHubTypes } from '../../../../enums'

export default {
  name: 'TemplateComponent',
  components: {
    TemplateItemsModal,
    Spinner,
    ConfirmModal
  },

  props: {
    vehicleBodyId: {
      type: String,
      required: true
    },
    quoteTimesType: {
      type: String,
      required: true
    },
    rates: {
      type: Array,
      required: true
    },
    quoteNo: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isTemplateItemsModalActive: false,
      selectedTemplateId: '',
      templatesLoading: false,
      templates: [],
      filter: {
        name: '',
        sortColumn: QuoteTemplateListColumns[0].name,
        sortOrder: QuoteTemplateListColumns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 200
      },

      isConfirmModalActive: false,
      templateDeleted: null,

      // when directly adding a template to the quote
      addTemplateConfirmModalActive: false,
      templateToAdd: null,
      templateItems: []
    }
  },
  computed: {
    // Add your computed properties here
    quoteTemplateTypeKeys() {
      return QuoteTemplateTypeKeys
    },
    permussions() {
      return this.$user.permissions?.find((p) => p.codeName === 'A073') || {}
    }
  },
  mounted() {
    this.getQuoteTemplates()
    // Add your mounted logic here
    this.$eventHub.$on(EventHubTypes.ReloadQuoteTemplate, () => {
      this.getQuoteTemplates()
    })
  },
  methods: {
    handleTemplateClick(template) {
      this.isTemplateItemsModalActive = true
      this.selectedTemplate = template
    },
    async getQuoteTemplate(id) {
      const quoteTemplates = await QuoteTemplateService.getSortedQuoteTemplateItems(id)
      this.templateItems = quoteTemplates
    },
    handleDeleteTemplateClick(template) {
      this.templateDeleted = template
      this.isConfirmModalActive = true
    },
    async deleteTemplate(isDelete = false) {
      if (!isDelete) {
        this.templateDeleted = null
        this.isConfirmModalActive = false
        return
      }
      await QuoteTemplateService.deleteQuoteTemplate(this.templateDeleted)
      this.$nextTick(() => {
        this.getQuoteTemplates()
        this.templateDeleted = null
        this.isConfirmModalActive = false
      })
    },
    handleAddTemplateClick(template) {
      this.templateToAdd = template
      this.addTemplateConfirmModalActive = true
      this.getQuoteTemplate(template.templateId)
    },
    addItemsFromTemplate() {
      this.addTemplateConfirmModalActive = false
      this.$emit('addRepairQuoteTemplateItems', this.templateToAdd.templateId)
    },
    cancelAddItems() {
      this.addTemplateConfirmModalActive = false
      this.templateToAdd = null
    },
    async getQuoteTemplates() {
      this.templatesLoading = true
      const quoteTemplates = await QuoteTemplateService.getQuoteTemplates(this.filter)
      const quoteRepairerTemplates = quoteTemplates.filter((template) => template.quoteType == this.quoteTemplateTypeKeys.Repairer)
      const quoteRepairerTemplatesWithoutLabour = quoteRepairerTemplates.filter(
        (template) =>
          (template.timesType == null && template.bodyId == null) ||
          (template.timesType == null && template.bodyId === this.vehicleBodyId) ||
          (template.bodyId == null && this.matchingTimesType(this.quoteTimesType, template.timesType))
      )
      const quoteRepairerTemplatesWithLabour = quoteRepairerTemplates.filter(
        (template) => template.bodyId === this.vehicleBodyId && this.matchingTimesType(this.quoteTimesType, template.timesType)
      )
      this.templates = [...quoteRepairerTemplatesWithLabour, ...quoteRepairerTemplatesWithoutLabour]
      this.templatesLoading = false
    },

    matchingTimesType(quoteTimesType, templateTimesType) {
      return (
        (quoteTimesType === 'LTAR' && templateTimesType === 'LTAR') ||
        (quoteTimesType === 'NTAR' && templateTimesType === 'NTAR') ||
        (quoteTimesType !== 'LTAR' && quoteTimesType !== 'NTAR' && templateTimesType && templateTimesType !== 'NTAR' && templateTimesType !== 'LTAR')
      )
    },

    handleClose() {
      this.isTemplateItemsModalActive = false
      this.selectedTemplate = null
    }
  }
}
</script>

<style lang="scss" scoped >
@import '@/assets/style/themes/default/variables';

.template-list-item {
  border-radius: 5px;
  background: lighten($grey, 40%);

  &:hover {
    // background-color: lighten($grey, 30%);
  }
}
</style>

